import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MultiselectItem, TableColumn, TableColumnEdit } from '@ui/aos-lib';
import { combineLatest, map, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  isSingleCommodityClient,
  selectNonCommunicationCommodities
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

import {
  commodityTableIcon,
  deviceTypeTableIcon,
  SEVERITY
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  FilterModel,
  ListingDataSource,
  ListingFiltersSource,
  ListingPageModel
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../shared/components/listing-table/listing-table.component';
import { DeviceListingFieldName, OrderDirection } from '../../equipment/devices/components/devices.models';
import { SeverityComponent } from './components/severity/severity.component';
import {
  EventConfiguration,
  EventConfigurationFieldName,
  EventConfigurationTable,
  SeverityTypeFilterValue,
  StatusFilterModelItems
} from './event-configuration.types';
import { EventConfigurationService } from './services/event-configuration.service';
import { DescriptionDetailedColumnComponent } from './components/description/description-detailed-column.component';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from '../../../services/permissions/permissions.types';
import {
  eventConfigurationTableSortField,
  EVENTS_SEARCH_MAX_LENGTH,
  EVENTS_SEARCH_MAX_WIDTH,
  EVENTS_SEARCH_TYPES
} from './event-configuration.constants';
import {
  EventConfigurationActivityType,
  EventConfigurationDeviceType,
  EventConfigurationSeverityRule,
  EventGroupsWithEventNamesResponse
} from './services/event-configuration-api.type';
import { getDateTimeItems } from '../../../shared/components/listing-page/helpers/filters.helper';
import { TableData } from '../../../shared/types/api-response.types';
import { EventConfigurationApiService } from './services/event-configuration-api.service';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { StatusDotColumnComponent } from 'src/app/shared/components/listing-table/components/status-dot-column.component';
import { IconColumnComponent } from 'src/app/shared/components/listing-table/components/icon-column.component';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';

const deviceTypeFilterModel: FilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.DEVICE_TYPE,
  items: [
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.ANY, value: EventConfigurationDeviceType.All },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.DCU, value: EventConfigurationDeviceType.DCU },
    {
      text: TranslateRegistry.get.FILTER.DEVICE_TYPE.EXACTAGUARD,
      value: EventConfigurationDeviceType.ExactaGuardGateway
    },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.METER, value: EventConfigurationDeviceType.Meter },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.MTU, value: EventConfigurationDeviceType.MTU },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.RMD, value: EventConfigurationDeviceType.RMDSensor },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.SBS, value: EventConfigurationDeviceType.BaseStation }
  ],
  selected: [
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.ANY, value: EventConfigurationDeviceType.All },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.DCU, value: EventConfigurationDeviceType.DCU },
    {
      text: TranslateRegistry.get.FILTER.DEVICE_TYPE.EXACTAGUARD,
      value: EventConfigurationDeviceType.ExactaGuardGateway
    },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.METER, value: EventConfigurationDeviceType.Meter },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.MTU, value: EventConfigurationDeviceType.MTU },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.RMD, value: EventConfigurationDeviceType.RMDSensor },
    { text: TranslateRegistry.get.FILTER.DEVICE_TYPE.SBS, value: EventConfigurationDeviceType.BaseStation }
  ],
  filterType: DeviceListingFieldName.DeviceType,
  isMultiselect: true,
  width: 160
};
const eventGroupFilterModel: FilterModel = {
  selected: [],
  filterType: EventConfigurationFieldName.EventGroup,
  isMultiselect: true,
  items: [],
  title: TranslateRegistry.get.FILTER.TITLE.EVENT_GROUP,
  width: 180
};
const lastEditedFilterModel: FilterModel = {
  selected: [getDateTimeItems()[0]],
  filterType: EventConfigurationFieldName.UpdatedOnUtc,
  isMultiselect: false,
  items: getDateTimeItems(),
  title: TranslateRegistry.get.FILTER.TITLE.LAST_EDITED,
  isNonSortable: true,
  width: 160
};
const severityFilterModel: FilterModel = {
  ...SEVERITY,
  selected: SEVERITY.items.map(item => ({ ...item })),
  filterType: EventConfigurationFieldName.SeverityLevel,
  items: SEVERITY.items.map(item => ({ ...item })),
  width: 160
};
const statusFilterModelItems: FilterModel['items'] = [
  { text: TranslateRegistry.get.FILTER.STATUS.ACTIVE, value: StatusFilterModelItems.ACTIVE },
  { text: TranslateRegistry.get.FILTER.STATUS.INACTIVE, value: StatusFilterModelItems.INACTIVE }
];
const statusFilterModel: FilterModel = {
  selected: statusFilterModelItems.map(item => ({ ...item })),
  filterType: EventConfigurationFieldName.IsEnabled,
  isMultiselect: true,
  items: statusFilterModelItems,
  title: TranslateRegistry.get.FILTER.TITLE.STATUS,
  width: 160
};
const commodityFilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.COMMODITY,
  items: [
    { text: TranslateRegistry.get.FILTER.COMMODITY.ANY_COMMODITY, value: 'Any' },
    { text: TranslateRegistry.get.FILTER.COMMODITY.GAS, value: 'Gas' },
    { text: TranslateRegistry.get.FILTER.COMMODITY.WATER, value: 'Water' },
    { text: TranslateRegistry.get.FILTER.COMMODITY.ELECTRIC, value: 'Electricity' }
  ],
  filterType: EventConfigurationFieldName.Commodity,
  isMultiselect: true,
  width: 160
};
const severityTypeFilterItems: FilterModel['items'] = [
  { text: TranslateRegistry.get.FILTER.SEVERITY_TYPE.ALL, value: SeverityTypeFilterValue.All },
  { text: TranslateRegistry.get.FILTER.SEVERITY_TYPE.SINGLE, value: SeverityTypeFilterValue.Single },
  { text: TranslateRegistry.get.FILTER.SEVERITY_TYPE.MULTIPLE, value: SeverityTypeFilterValue.Multiple }
];
const severityTypeFilterModel: FilterModel = {
  selected: [severityTypeFilterItems[0]],
  filterType: EventConfigurationFieldName.SeverityType,
  isMultiselect: false,
  isNonSortable: true,
  items: severityTypeFilterItems,
  title: TranslateRegistry.get.FILTER.TITLE.SEVERITY_TYPE,
  width: 160
};

const editColumnConfig: TableColumnEdit = {
  clickCallbackFn(): void {},
  field: 'edit',
  isEdit: true,
  reorderable: false,
  sortable: false,
  sticky: true,
  title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.EDIT,
  width: 64
};

@Injectable({ providedIn: 'root' })
export class EventConfigurationListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  readonly permissions = inject(PermissionsService);
  private readonly eventConfigurationService = inject(EventConfigurationService);
  private readonly eventConfigurationApiService = inject(EventConfigurationApiService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #store = inject(Store);
  readonly #isSingleCommodityClient = this.#store.selectSignal(isSingleCommodityClient);

  readonly searchTypeList = computed(() => EVENTS_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => EVENTS_SEARCH_TYPES[0]);
  readonly searchTextMaxLength = EVENTS_SEARCH_MAX_LENGTH;
  readonly searchMaxWidth = EVENTS_SEARCH_MAX_WIDTH;
  readonly sorting = computed(() => ({
    columnName: EventConfigurationFieldName.EventGroup,
    orderDirection: OrderDirection.ASC
  }));
  readonly tableColumnsDetailed: TableColumn[] = [
    {
      field: 'eventGroup',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.EVENT_GROUP,
      width: 158,
      sortable: true
    },
    { field: 'name', title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.EVENT_NAME, width: 236, sortable: true },
    {
      field: 'description',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.DESCRIPTION,
      width: 317,
      sortable: false,
      cmp: DescriptionDetailedColumnComponent
    },
    {
      field: 'critical',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.CRITICAL,
      width: 80,
      sortable: false,
      cmp: SeverityComponent
    },
    {
      field: 'severe',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.SEVERE,
      width: 80,
      sortable: false,
      cmp: SeverityComponent
    },
    {
      field: 'warning',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.WARNING,
      width: 80,
      sortable: false,
      cmp: SeverityComponent
    },
    {
      field: 'notice',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.NOTICE,
      width: 80,
      sortable: false,
      cmp: SeverityComponent
    },
    { field: 'unitOfMeasure', title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.UNIT, width: 58, sortable: true },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.DEVICE_TYPE,
      width: 128,
      sortable: true,
      icon: deviceTypeTableIcon
    },
    {
      field: 'commodity',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.COMMODITY,
      width: 122,
      sortable: true,
      icon: commodityTableIcon,
      cmp: IconColumnComponent
    },
    {
      field: 'eventConfigurationStatus',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.STATUS,
      width: 78,
      sortable: true,
      cmp: StatusDotColumnComponent
    },
    {
      field: 'lastUpdateTimestamp',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.LAST_EDITED,
      width: 158,
      sortable: true
    },
    {
      field: 'updatedBy',
      title: TranslateRegistry.get.EVENT_CONFIGURATION.TABLE.UPDATED_BY,
      width: 148,
      sortable: true
    },
    editColumnConfig
  ];
  readonly hasEditPermission = toSignal(
    this.permissions.hasRight(ResourceValue.EVENT_CONFIGURATION, PermissionValue.EDIT)
  );
  readonly tableColumns = computed(() => {
    const hasEditPermission = this.hasEditPermission();
    const isSingleCommodity = this.#isSingleCommodityClient();

    const filtered = hasEditPermission
      ? this.tableColumnsDetailed
      : this.tableColumnsDetailed.filter(col => col.field !== 'edit');
    return filtered.map(col => (col.field === 'commodity' ? { ...col, isHidden: isSingleCommodity } : col));
  });
  readonly dataSource: ListingDataSource<EventConfigurationTable> = {
    getAll: tableState => this.eventConfigurationService.getTableData(tableState),
    total: 0
  };
  readonly pageTitle = TranslateRegistry.get.EVENT_CONFIGURATION.TITLE.EVENT_CONFIGURATION;
  readonly tableCmp = ListingTableComponent;
  readonly sortField = eventConfigurationTableSortField;

  readonly exportCsv = {
    fileName: 'Export-EventConfiguration',
    mapData: (data: TableData<EventConfiguration>): ExportCsvConfig[] => {
      const isSingleCommodity = this.#isSingleCommodityClient();
      return data.data.map(item => {
        const isSingleMode = item.trigger.activityType === EventConfigurationActivityType.Alarm;
        const getSeverityValue = (field: 'critical' | 'severe' | 'warning' | 'notice'): string => {
          const rule = item[field] as EventConfigurationSeverityRule;
          return isSingleMode ? rule.severityLevel : rule.threshold?.toString() || '';
        };
        return {
          eventGroup: { title: 'EventGroup', value: item.eventGroup },
          name: { title: 'EventName', value: item.name },
          description: { title: 'Description', value: item.description },
          critical: { title: 'Critical', value: getSeverityValue('critical') },
          severe: { title: 'Severe', value: getSeverityValue('severe') },
          warning: { title: 'Warning', value: getSeverityValue('warning') },
          notice: { title: 'Notice', value: getSeverityValue('notice') },
          unitOfMeasure: { title: 'Unit', value: item.unitOfMeasure },
          deviceTypeName: { title: 'DeviceType', value: item.deviceTypeName },
          ...(!isSingleCommodity && { commodity: { title: 'Commodity', value: item.commodity } }),
          eventConfigurationStatus: { title: 'Status', value: item.eventConfigurationStatus },
          lastUpdateTimestamp: { title: 'LastEdited', value: item.lastUpdateTimestamp },
          updatedBy: { title: 'UpdatedBy', value: item.updatedBy }
        };
      });
    }
  };
  readonly pageId = ListingPageId.EventConfiguration;
  readonly filterSource: ListingFiltersSource = {
    getAll: () =>
      combineLatest([
        this.eventConfigurationApiService.getEventGroupsWithEventNames(),
        this.#store.select(selectNonCommunicationCommodities).pipe(take(1))
      ]).pipe(
        map(([eventGroupItems, userCommodities]: [EventGroupsWithEventNamesResponse[], readonly string[]]) => {
          const multiselectItems: MultiselectItem[] = eventGroupItems.map(({ groupName }) => ({
            text: translationKeysMap.get(groupName) || groupName,
            value: groupName
          }));
          const eventGroupFilter: FilterModel = {
            ...eventGroupFilterModel,
            items: multiselectItems,
            selected: multiselectItems
          };
          const commodityItems = commodityFilterModel.items.filter(
            ({ value }) => value === 'Any' || userCommodities.includes(value)
          );
          const dynamicCommodityFilter: FilterModel = {
            ...commodityFilterModel,
            items: commodityItems,
            selected: commodityItems
          };

          return [
            eventGroupFilter,
            severityFilterModel,
            deviceTypeFilterModel,
            ...(this.#isSingleCommodityClient() ? [] : [dynamicCommodityFilter]),
            statusFilterModel,
            lastEditedFilterModel,
            severityTypeFilterModel
          ];
        })
      ),
    getReportGroup: () => of()
  };

  constructor() {
    super();
    this.listingPageModelRegistry.add(this.pageId, this);
  }
}
